import { Link } from "react-router-dom";
import { Phone, ArrowRight, AlertTriangle, Camera } from "lucide-react";
import { SITE } from "@/config/site";
import { trackEvent } from "@/lib/analytics";
import { Reveal } from "@/components/Reveal";

// Boutons réutilisables --------------------------------------------------
export const CallButton = ({ location = "generic", label, testid = "call-btn", className = "" }) => (
  <a
    href={SITE.phoneHref}
    onClick={() => trackEvent("click_phone", { location })}
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-gold text-brand-black font-bold hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-10px_rgba(245,180,0,0.7)] transition-all ${className}`}
  >
    <Phone className="w-5 h-5" /> {label || `Appeler le ${SITE.phoneDisplay}`}
  </a>
);

export const InterventionButton = ({ label = "Demander une intervention", testid = "intervention-btn", dark = false, className = "" }) => (
  <Link
    to="/contact"
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-bold transition-all hover:-translate-y-0.5 ${
      dark ? "bg-brand-black text-white hover:bg-black/80" : "border-2 border-brand-black text-brand-black hover:bg-brand-black hover:text-white"
    } ${className}`}
  >
    {label} <ArrowRight className="w-5 h-5" />
  </Link>
);

// En-tête de section -----------------------------------------------------
export const SectionHeading = ({ eyebrow, title, sub, center = false, light = false }) => (
  <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-3xl"} mb-12 md:mb-16`}>
    {eyebrow && (
      <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-brand-gold mb-3">{eyebrow}</span>
    )}
    <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] ${light ? "text-white" : "text-brand-black"}`}>
      {title}
    </h2>
    {sub && <p className={`mt-4 text-base lg:text-lg leading-relaxed ${light ? "text-white/70" : "text-brand-text"}`}>{sub}</p>}
  </div>
);

// Bloc d'urgence ---------------------------------------------------------
export const UrgencyBlock = () => (
  <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-10 md:-mt-16 relative z-20">
    <Reveal className="bg-brand-black text-white rounded-3xl p-7 md:p-12 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] grain-overlay overflow-hidden">
      <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-brand-gold/15 text-brand-gold px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <AlertTriangle className="w-4 h-4" /> Sécurité avant tout
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4">Vous avez découvert un nid ?</h2>
          <p className="text-white/80 leading-relaxed max-w-2xl">
            Ne vous approchez pas, ne bouchez pas l'entrée du nid et n'essayez pas de le détruire vous-même. Éloignez les
            enfants et les animaux, puis contactez-nous pour évaluer la situation.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:min-w-[240px]">
          <a
            href={SITE.phoneHref}
            onClick={() => trackEvent("click_phone", { location: "urgency" })}
            data-testid="urgency-call-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gold text-brand-black font-bold hover:-translate-y-0.5 transition-transform"
          >
            <Phone className="w-5 h-5" /> Appeler maintenant
          </a>
          <Link
            to="/contact"
            data-testid="urgency-photo-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border-2 border-white/30 text-white font-bold hover:bg-white/10 transition-colors"
          >
            <Camera className="w-5 h-5" /> Demander une intervention
          </Link>
        </div>
      </div>
    </Reveal>
  </section>
);

// Appel à l'action final -------------------------------------------------
export const FinalCTA = () => (
  <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
    <Reveal className="bg-brand-gold rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
      <div className="relative z-10 max-w-3xl mx-auto">
        <h2 className="font-display text-3xl md:text-5xl font-extrabold text-brand-black tracking-tight">
          Besoin d'une intervention contre des nuisibles ?
        </h2>
        <p className="mt-4 text-brand-black/80 text-base md:text-lg leading-relaxed">
          Décrivez-nous la situation en quelques secondes. Vous pouvez également joindre des photos pour faciliter la
          première évaluation.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={SITE.phoneHref}
            onClick={() => trackEvent("click_phone", { location: "final_cta" })}
            data-testid="final-call-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-black text-white font-bold hover:-translate-y-0.5 transition-transform"
          >
            <Phone className="w-5 h-5" /> Appeler
          </a>
          <Link
            to="/contact"
            data-testid="final-callback-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-brand-black font-bold hover:-translate-y-0.5 transition-transform"
          >
            Être rappelé
          </Link>
          <Link
            to="/contact"
            data-testid="final-form-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border-2 border-brand-black text-brand-black font-bold hover:bg-brand-black hover:text-white transition-colors"
          >
            Remplir le formulaire <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </Reveal>
  </section>
);
